import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Link,
  FormHelperText
} from "@material-ui/core";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import { remCalc } from "../../utils/helpers";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import API, { API_NO_TOKEN } from "../../utils/axios";
import LoadingBar from "./LoadingBar";
import clsx from "clsx";
import { login } from "../../store/actions/auth";
import FullSreenDialog from "./FullSreenDialog";
import { emailRegex } from "../../utils/regExps";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import OptimizedTooltip from "../common/OptimizedTooltip";
import history from "../../utils/history";
import { useSnackbar } from "notistack";
import { LANDING_URL } from "../data/constants";
import { setCookie } from "../../utils/cookies";

const useStyles = makeStyles(theme => ({
  textField: {
    width: "100%",
    marginTop: theme.spacing(0.75),
    "&:first-of-type": {
      marginTop: theme.spacing(3)
    },
    // change selection color to primary
    "& input::selection": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff"
    }
  },
  signinWrapper: {
    boxSizing: "border-box",
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(6, 5, 5),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#fff",
    boxShadow: "0px 0px 12px rgba(0, 0, 0, 0.08)",
    width: remCalc(408),
    height: remCalc(486),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: theme.spacing(0),
      boxShadow: "unset"
    }
  },
  signinHeader: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
      lineHeight: "2rem"
    }
  },
  signinText: {
    lineHeight: "1.75rem",
    fontSize: "1rem"
  },
  link: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    cursor: "pointer",
    lineHeight: "1.5rem",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline"
    }
  },
  linkBtn: {
    textDecoration: "none !important"
  },
  button: {
    height: 44,
    textTransform: "none",
    boxShadow: "none !important",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    fontSize: "0.9375rem"
  },
  buttonSimple: {
    height: 44,
    textTransform: "none",
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: "0.9375rem"
  },
  labelTextShrinked: {
    fontSize: "1rem",
    lineheight: "1.5rem"
  },
  helperText: {
    margin: theme.spacing(0.5, 0),
    paddingLeft: theme.spacing(1.75),
    fontSize: "0.75rem"
  },
  helperTextAuth: {
    margin: 0
  }
}));

function SignIn({ login, userType, userLoaded }) {
  const classes = useStyles();

  const [inputValues, setInputValues] = useState({
    email: "",
    password: ""
  });

  const theme = useTheme();
  const screenSm = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true
  });
  const { enqueueSnackbar } = useSnackbar();
  const [errorValues, setErrorValues] = useState({
    email: "",
    password: ""
  });

  useEffect(() => {
    sessionStorage.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userLoaded) {
      if (!userType) {
        if (
          LANDING_URL.includes("fojw") ||
          process.env.NODE_ENV === "development"
        ) {
          history.push("/login");
        } else {
          window.location.href = `${LANDING_URL}/login`;
        }
      }
    }
  }, [userLoaded, userType]);

  const [loading, setLoading] = useState(false);

  // 1st step after submit: validate inputs and execute recaptcha
  const handleSubmit = e => {
    e.preventDefault();
    if (
      emailRegex.test(inputValues.email) &&
      inputValues.email &&
      inputValues.password
    ) {
      setErrorValues({ email: "", password: "", authError: "" });

      onResolved(true);
    } else {
      if (!emailRegex.test(inputValues.email) || !inputValues.email) {
        setErrorValues({
          password: "",
          authError: "",
          email: "Enter a valid email address"
        });
      } else {
        setErrorValues({ email: "", password: "Enter a password" });
      }
    }
  };

  // 2nd step after submit: after successfull recaptcha send signin request to api
  const onResolved = data => {
    if (data) {
      setLoading(true);
      API_NO_TOKEN.post("/v1/users/signin", {
        email: inputValues.email,
        password: inputValues.password
      })
        .then(res => {
          setLoading(false);
          
          // Update axios instance Authorization
          API.defaults.headers.common["Authorization"] =
            "Bearer " + res.data.accessToken;
          setCookie("accessToken", "Bearer " + res.data.accessToken, 1);
          login(
            res.data.accessToken,
            res.data.expiresAt,
            res.data.refreshToken
          );
          history.push("/");
        })
        .catch(err => {
          console.log(err);
          console.log(err.response.status);
          if (err.response && err.response.status === 401) {
            enqueueSnackbar(
              err.response.localizedMessage ||
                "Invalid email/password combination",
              {
                variant: "error",
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "center"
                },
                autoHideDuration: 5000
              }
            );
          }
          setLoading(false);
        });
    } else {
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  return (
    <FullSreenDialog>
      <Box
        className={classes.signinWrapper}
        component="form"
        display="flex"
        flexDirection="column"
        noValidate
        mb={15}
        onSubmit={handleSubmit}
      >
        {loading && <LoadingBar />}
        {/* Header */}
        <Box>
          <Typography variant="h5" className={classes.signinHeader}>
            Log in
          </Typography>
          <Typography variant="body1" className={classes.signinText}>
            to continue to Routetitan
          </Typography>
        </Box>

        {/* Inputs */}
        <Box>
          <TextField
            name="email"
            variant="outlined"
            type="email"
            value={inputValues.email}
            onChange={handleChange}
            label="Email"
            className={classes.textField}
            autoFocus={!screenSm}
            helperText={errorValues.email || " "}
            error={!!errorValues.email}
            required
            InputLabelProps={{
              required: false,
              classes: { shrink: classes.labelTextShrinked }
            }}
            FormHelperTextProps={{ classes: { root: classes.helperText } }}
          />
          <TextField
            name="password"
            variant="outlined"
            type={showPassword ? "text" : "password"}
            value={inputValues.password}
            onChange={handleChange}
            label="Password"
            className={classes.textField}
            helperText={errorValues.password || " "}
            error={!!errorValues.password}
            required
            InputLabelProps={{
              required: false,
              classes: { shrink: classes.labelTextShrinked }
            }}
            FormHelperTextProps={{ classes: { root: classes.helperText } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <OptimizedTooltip
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? (
                        <Visibility />
                      ) : (
                        <VisibilityOff style={{ color: "#DADADA" }} />
                      )}
                    </IconButton>
                  </OptimizedTooltip>
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Forgot password */}
        <Box
          display="flex"
          justifyContent="flex-end"
          mt={-0.25}
          alignItems="center"
        >
          <Typography
            component={"p"}
            onClick={() => {
              history.push("/reset-password");
            }}
            variant="body2"
            className={classes.link}
          >
            Forgot password?
          </Typography>
        </Box>

        {/* Actions */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt="auto"
        >
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            type="submit"
          >
            Log In
          </Button>
        </Box>
      </Box>
    </FullSreenDialog>
  );
}

const mapStateToProps = state => ({
  userType: state.auth.userData.userType,
  userLoaded: state.auth.userLoaded
});

export default connect(mapStateToProps, { login })(SignIn);
